import type {
  CumulatedRouteConfig,
  RenderRouteConfig,
  RouterObject,
  RouterOptions,
  SubRouterRouteConfig,
} from '@adornis/router/client/make-router.js';
import { makeRouter } from '@adornis/router/client/make-router.js';

/**
 * Drop-in replacement for `makeRouter` that keeps the browser tab title static.
 *
 * `@adornis/router` builds `document.title` per route and falls back to the route name when a route
 * has no `documentTitlePart`, which would show internal names like "root" or "list-persons". We only
 * want the app name, which the root router supplies via `routerDocumentTitle`, so every route gets an
 * empty title part unless it explicitly sets one.
 */
export function makeChancenRouter<PathName extends string, RenderReturnType = any>(
  routes: Record<
    PathName,
    | SubRouterRouteConfig<any, RenderReturnType>
    | RenderRouteConfig<any, RenderReturnType>
    | CumulatedRouteConfig<any, RenderReturnType>
  >,
  config?: RouterOptions,
): RouterObject<PathName, RenderReturnType> {
  const routesWithoutTitleParts = Object.fromEntries(
    Object.entries(routes).map(([name, route]) => [name, { documentTitlePart: '', ...(route as object) }]),
  ) as typeof routes;

  return makeRouter<PathName, RenderReturnType>(routesWithoutTitleParts, config);
}
